import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { EnableSignUpSessionRecording } from '~/constants/ff.posthog';
import { useFeatureFlag } from './useFeatureFlag';
import { usePosthog } from './usePosthog';

// Old sign up flow (post account-creation steps)
const REGISTRATION_PATH = '/registration';
// New sign up flow redesign (AuthRedesign) — equivalent to the $pageview of /signup
const SIGNUP_REDESIGN_PATH = '/signup';

export const useSignUpPosthogSessionRecording = () => {
  const location = useLocation();
  const posthog = usePosthog();
  const isRecordingRef = useRef(false);
  const hasStartedSignupRef = useRef(false);
  const sessionIdRef = useRef<string | undefined>(undefined);
  const isEnabled = useFeatureFlag(EnableSignUpSessionRecording);

  useEffect(() => {
    // PostHog rotates the session id on reset() (called on logout) and on session
    // timeout. The recording state below is scoped to a single session, so re-arm it
    // whenever the session changes — otherwise a second sign up in the same tab (after
    // logging out of the first) would never start a new recording.
    const currentSessionId = posthog.getSessionId();
    if (currentSessionId !== sessionIdRef.current) {
      sessionIdRef.current = currentSessionId;
      isRecordingRef.current = false;
      hasStartedSignupRef.current = false;
    }

    const isOnSignupFlow =
      location.pathname.startsWith(REGISTRATION_PATH) ||
      location.pathname.startsWith(SIGNUP_REDESIGN_PATH);

    // Track if user entered the sign up flow (old or new) this session
    if (isEnabled && isOnSignupFlow) {
      hasStartedSignupRef.current = true;
    }

    // Start recording if enabled and user is on registration or has been through signup this session
    const shouldRecord = isEnabled && hasStartedSignupRef.current;

    if (shouldRecord && !isRecordingRef.current) {
      posthog.startSessionRecording();
      isRecordingRef.current = true;
    }
  }, [location.pathname, posthog, isEnabled]);

  // Stop recording on unmount only (when browser/tab closes)
  useEffect(() => {
    return () => {
      posthog.stopSessionRecording();
    };
  }, [posthog]);
};
